// ReferralDrawer.tsx

import { useSlice } from "@bridge-applications/provider-kit";
import { useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { hasSpinEnergy } from "../../../logic/helpers/slotMachineState";
import type { ReferralCampaignController } from "../../../logic/hooks/useReferralCampaign";
import { BottomDrawer } from "../../common/BottomDrawer/BottomDrawer";
import { PurchaseEnergyBundleContent } from "./PurchaseEnergyBundleContent";
import { RewardedReferralContent } from "./RewardedReferralContent";
import { haptics } from "../../../logic/helpers/haptics";

const PURCHASE_UNAVAILABLE_TOAST_ID = "purchase-unavailable";

interface ReferralDrawerProps {
    campaign: ReferralCampaignController;
    isOpen: boolean;
    onClose: () => void;
    startOnPurchase?: boolean;
}

export function ReferralDrawer({
    campaign,
    isOpen,
    onClose,
    startOnPurchase = false,
}: ReferralDrawerProps) {
    const { t } = useTranslation();
    const { state: authState } = useSlice("authSlice");
    const { state: slotState } = useSlice("slotMachineSlice");
    const isAuthReady = authState.bootstrapStatus === "ready";
    const isAuthenticating =
        authState.bootstrapStatus === "idle" ||
        authState.bootstrapStatus === "authenticating";
    const hasEnergy = hasSpinEnergy(slotState.snapshot);
    const [showPurchase, setShowPurchase] = useState(startOnPurchase);

    useLayoutEffect(() => {
        if (isOpen) setShowPurchase(startOnPurchase);
    }, [isOpen, startOnPurchase]);

    const referral = campaign.referral;
    const requiredCount = referral?.requiredCount ?? 2;
    const qualifiedCount = Math.min(
        referral?.qualifiedCount ?? 0,
        requiredCount,
    );
    const hasSuccessfulShare =
        campaign.hasLocalShareHint || Boolean(referral?.shareReportedAt);
    const showRewarded = hasSuccessfulShare && !showPurchase;
    const isRewarded = referral?.state === "REWARDED";
    const isUnavailable = referral?.state === "UNAVAILABLE";
    const isBusy = campaign.isPreparing || campaign.isReportingShare;
    const shareDisabled =
        !isAuthReady ||
        isBusy ||
        !campaign.isShareReady ||
        isUnavailable ||
        isRewarded;

    let shareLabel = t("referralDrawer.share");
    if (isAuthenticating) {
        shareLabel = t("referralDrawer.signingIn");
    } else if (!isAuthReady) {
        shareLabel = t("referralDrawer.unavailable");
    } else if (isRewarded) {
        shareLabel = t("referralDrawer.completed");
    } else if (isUnavailable) {
        shareLabel = t("referralDrawer.unavailable");
    } else if (campaign.isReportingShare) {
        shareLabel = t("referralDrawer.confirming");
    } else if (campaign.isPreparing || !campaign.isShareReady) {
        shareLabel = t("referralDrawer.preparing");
    }

    const authMessage =
        !isAuthReady && !isAuthenticating
            ? t("referralDrawer.openInTelegram")
            : null;
    const message = campaign.error ?? authMessage;

    // This starter bundle remains unavailable; backend offers need their own dialog.
    const handlePurchase = () => {
        toast.error(t("purchaseEnergy.purchase.unavailable"), {
            id: PURCHASE_UNAVAILABLE_TOAST_ID,
        });
    };

    const handlePurchaseShare = () => {
        if (hasSuccessfulShare) {
            // The user has already completed Telegram's first successful
            // share. Return to the referral-progress screen, where they can
            // share again, instead of reopening Telegram from this screen.
            setShowPurchase(false);
            return;
        }
        campaign.share();
    };

    return (
        <BottomDrawer
            className={
                showRewarded
                    ? "rewarded-referral-drawer"
                    : "purchase-energy-drawer"
            }
            isOpen={isOpen}
            onClose={() => {
                haptics.impact("light");
                onClose();
            }}
            aria-label={
                showRewarded
                    ? t("referralDrawer.rewardedAriaLabel")
                    : t("referralDrawer.purchaseAriaLabel")
            }
            content={
                showRewarded ? (
                    <RewardedReferralContent
                        qualifiedCount={qualifiedCount}
                        requiredCount={requiredCount}
                        rewardAmount={referral?.reward.amount ?? "50"}
                        isRewarded={isRewarded}
                        onBack={() => setShowPurchase(true)}
                        onRefresh={() => void campaign.refreshStatus()}
                        onShare={campaign.share}
                        refreshDisabled={campaign.isLoading}
                        shareDisabled={shareDisabled}
                        shareLabel={shareLabel}
                        message={message}
                    />
                ) : (
                    <PurchaseEnergyBundleContent
                        hasEnergy={hasEnergy}
                        onPurchase={handlePurchase}
                        onShare={handlePurchaseShare}
                        hasSuccessfulShare={hasSuccessfulShare}
                        qualifiedCount={qualifiedCount}
                        requiredCount={requiredCount}
                        shareDisabled={
                            hasSuccessfulShare ? false : shareDisabled
                        }
                        shareLabel={shareLabel}
                        message={message}
                    />
                )
            }
        />
    );
}
